import { CompanyType } from 'Models'
import { CompanyLocationType } from 'Models/companyType'
import React, { Key, ChangeEvent, useState } from 'react'
import { FieldErrors, UseFormRegister } from 'react-hook-form'
import { countryType, stateType } from '../../DeliveryUltra/DeliveryUltra'
import { CreatePlpType } from './CreatePlp'
import './CreatePlp.scss'
import { productListType } from 'Models/productType'
import lga from 'Data/lgaNG.json'

type FormProps = {
  children?: React.ReactNode
  errors: FieldErrors<CreatePlpType>
  register: UseFormRegister<CreatePlpType>
  companies: Array<CompanyType>
  locations: Array<CompanyLocationType>
  locationsPrice: Array<productListType>
  selectState: (value: string) => void
  states: { id: string; state_code: string; name: string }[]
  onNext: () => void
  publicAccess?: boolean
  verifyMsisdn: (e: ChangeEvent<HTMLInputElement>) => void
  msisdnErrors: boolean | undefined
  msisdnDetails?: string
  hideNext?: boolean
}

const PlpCompanyDetailsStep: React.FC<FormProps> = ({
  errors,
  register,
  selectState,
  states,
  onNext,
  publicAccess,
  verifyMsisdn,
  msisdnErrors,
  msisdnDetails,
  hideNext,
}) => {
  const [fieldErrors, setFieldErrors] = useState({
    companyName: false,
    cacNumber: false,
    companyAddress: false,
    companyState: false,
    companyLGA: false,
  })

  const validateNotEmpty = (value: string) => value.trim() !== ''

  const updateFieldError = (field: string, value: string) => {
    setFieldErrors((prev) => ({
      ...prev,
      [field]: validateNotEmpty(value),
    }))
  }

  const handleInputChange = (field: string) => (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    updateFieldError(field, value)
  }

  const isNextButtonDisabled = Object.values(fieldErrors).every((error) => error === true)

  const [lgaList, setLgaList] = useState<Array<{ id: string; state_id: string; name: string }>>([])

  const selectLga = (stateName: { target: { value: string } }) => {
    console.log(stateName.target.value)

    if (stateName) {
      const stateId = states.find((res) => res.name === stateName.target.value)?.id

      const lgas = lga.filter((res) => res.state_id === stateId)
      setLgaList(lgas)
    }
  }

  return (
    <div className='row'>
      <div className='col-sm-6 my-4'>
        <div className='step-title'>Fill the form below</div>
      </div>
      {(hideNext === true || hideNext === undefined) && (
        <div className='col-sm-6 text-sm-end my-4'>
          <div className='step-page-number'>Page 1 of 3</div>
        </div>
      )}

      <div className={publicAccess ? 'col-md-8' : 'col-md-6'}>
        <div className='form-group mb-3'>
          {(hideNext === true || hideNext === undefined) && (
            <label htmlFor='customerMsisdn'>Customer Msisdn or Phone</label>
          )}
          <input
            type={hideNext === true || hideNext === undefined ? 'text' : 'hidden'}
            defaultValue={hideNext !== undefined && hideNext === false ? '+2348022471355' : ''}
            id='customerMsisdn'
            className={errors.customerMsisdn ? 'form-control is-invalid' : 'form-control'}
            {...register('customerMsisdn', { onChange: verifyMsisdn })}
            placeholder=''
          />
          {errors.customerMsisdn && (
            <div className='error text-danger'>{errors.customerMsisdn.message}</div>
          )}
          {msisdnErrors && <div className='error text-danger'>Phone number does not exist</div>}
          {msisdnDetails && <div className='error text-success'>{msisdnDetails}</div>}
          {(hideNext === undefined || hideNext === true) && publicAccess && (
            <small className='text-secondary'>
              Customer phone number must contain contry code.
            </small>
          )}
        </div>

        <div className='form-group mb-3'>
          <label htmlFor='companyName'>Company Name</label>
          <input
            type='text'
            id='companyName'
            className={errors.companyName ? 'form-control is-invalid' : 'form-control'}
            {...register('companyName', { onChange: handleInputChange('companyName') })}
          />
          {errors.companyName && (
            <div className='error text-danger'>{errors.companyName.message}</div>
          )}
        </div>

        <div className='form-group mb-3'>
          <label htmlFor='cacNumber'>Provide your BN or RC number (CAC number)</label>
          <input
            type='text'
            id='cacNumber'
            className={errors.cacNumber ? 'form-control is-invalid' : 'form-control'}
            {...register('cacNumber', { onChange: handleInputChange('cacNumber') })}
          />
          {errors.cacNumber && <div className='error text-danger'>{errors.cacNumber.message}</div>}
        </div>

        <div className='row'>
          <div className='col-12'>
            <div className='form-group mb-3'>
              <label htmlFor='address'>Filling Station Address</label>
              <input
                type='text'
                id='address'
                className={errors.companyAddress ? 'form-control is-invalid' : 'form-control'}
                {...register('companyAddress', { onChange: handleInputChange('companyAddress') })}
              />
              {errors.companyAddress && (
                <div className='error text-danger'>{errors.companyAddress.message}</div>
              )}
            </div>
          </div>

          <div className='col-12'>
            <div className='form-group mb-3'>
              <label htmlFor='state'>
                State <span className='text-danger'>*</span>
              </label>
              <select
                id='state'
                className={errors.companyState ? 'form-control is-invalid' : 'form-control'}
                {...register('companyState', {
                  onChange: (e) => {
                    // call the curried handler with the event so it actually runs
                    handleInputChange('companyState')(e)
                    // update LGA list based on selected state
                    selectLga(e)
                    // inform parent/other logic about selected state value
                    selectState(e.target.value)
                  },
                })}
              >
                <option>--State--</option>
                {states.length > 0 &&
                  states.map(
                    (
                      result: { id: string; state_code: string; name: string },
                      index: React.Key,
                    ) => {
                      return (
                        result.name !== 'Nationwide' && <option key={index}>{result.name}</option>
                      )
                    },
                  )}
              </select>
              {errors.companyState && (
                <div className='error text-danger'>{errors.companyState.message}</div>
              )}
            </div>
          </div>

          <div className='col-12'>
            <div className='form-group mb-3'>
              <label htmlFor='companyLGA'>
                Local Government Area (LGA) <span className='text-danger'>*</span>
              </label>

              <select
                id='companyLGA'
                className={errors.companyLGA ? 'form-control is-invalid' : 'form-control'}
                {...register('companyLGA', { onChange: handleInputChange('companyLGA') })}
              >
                <option>--LGA--</option>
                {lgaList.length > 0 &&
                  lgaList.map(
                    (result: { id: string; state_id: string; name: string }, index: React.Key) => {
                      return <option key={index}>{result.name}</option>
                    },
                  )}
              </select>
              {errors.companyLGA && (
                <div className='error text-danger'>{errors.companyLGA.message}</div>
              )}
            </div>
          </div>
        </div>

        {(hideNext === true || hideNext === undefined) && (
          <button onClick={onNext} className='btn btn-respond-req' disabled={!isNextButtonDisabled}>
            Next
          </button>
        )}
      </div>
    </div>
  )
}

export default PlpCompanyDetailsStep
